import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { AdminPageHeader, AdminPageShell, NoticePanel, StatCard } from './AdminPageUI';

const ManageClasses = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [newClass, setNewClass] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.CLASSES);
      if (!response.ok) throw new Error('Failed to load classes');
      setClasses(await response.json());
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClass = async () => {
    const className = newClass.trim();
    if (!className) {
      alert('Class name cannot be empty');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.CLASSES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ className })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to add class');
      setNewClass('');
      fetchClasses();
    } catch (error) {
      console.error('Error adding class:', error);
      alert(error.message);
    }
  };

  const handleDeleteClass = async (className) => {
    if (!window.confirm(`Delete ${className}? Check linked students, subjects, and timetables before removing a class.`)) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.CLASSES}/${encodeURIComponent(className)}`, { method: 'DELETE' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to delete class');
      fetchClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      alert(error.message);
    }
  };

  const filteredClasses = classes.filter((className) =>
    className.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminPageShell>
      <AdminPageHeader
        eyebrow="Academic Setup"
        title="Manage Classes"
        description="Add, review, and maintain class names used by students, subjects, exams, attendance, and timetables."
        actions={(
          <button onClick={() => navigate('/dashboard')} className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700">
            Back to Dashboard
          </button>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Total Classes" value={classes.length} tone="sky" detail="Available class groups" />
        <StatCard label="Filtered View" value={filteredClasses.length} tone="emerald" detail="Matching current search" />
        <StatCard label="Naming Status" value={classes.length ? 'Ready' : 'Empty'} tone="amber" detail="Used across academic modules" />
      </div>

      <NoticePanel title="Class setup checklist" tone="sky">
        Create classes before assigning subjects, lessons, exams, student records, and timetable periods. Avoid deleting active classes that already have linked records.
      </NoticePanel>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Enter new class name"
            value={newClass}
            onChange={(e) => setNewClass(e.target.value)}
            className="border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
          <button onClick={handleAddClass} className="bg-emerald-600 text-white px-5 py-3 rounded-lg font-bold hover:bg-emerald-700">
            Add Class
          </button>
          <input
            type="text"
            placeholder="Search classes"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-slate-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-black text-slate-900">Class Directory</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading classes...</div>
        ) : filteredClasses.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No classes found.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredClasses.map((className) => (
              <div key={className} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 hover:bg-slate-50">
                <div>
                  <p className="font-black text-slate-900">{className}</p>
                  <p className="text-sm text-slate-500">Class record for academic content, attendance, and scheduling.</p>
                </div>
                <button
                  onClick={() => handleDeleteClass(className)}
                  className="bg-rose-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-rose-700"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminPageShell>
  );
};

export default ManageClasses;
